import {
  getOrganizationCountry,
  getStateAndPostCodePerContact,
  getPurchaseOwnerOccupiedStatus
} from '__core/shared/util/salestrekker';
/* @ngInject */

export default (
  $q,
  $rootScope,
  financesService,
  helperService,
  incomeService,
  expensesService,
  householdsService,
  Liability,
  ContactMaritalStatusOption,
  HouseholdExpenseType,
  OwnershipType,
  ASSET_FINANCE
) => {
  /**
   * Get/set monthly financial position.
   *
   * @param {Object} workflow
   *
   * @returns {String}
   */
  const getMonthlyFinancialPositionAnnouncement = ({ workflow }) => {
    let announcement = '* Buffer is calculated as: ';
    let hasPrimaryIncomeBuffer = false;
    let hasPrimaryExistingRepaymentBuffer = false;
    let hasPrimaryProposedRepaymentBuffer = false;
    if (workflow?.isAssetFinance()) {
      hasPrimaryIncomeBuffer = $rootScope.CurrentOrganization.hasPrimaryIncomeBufferAF();
      hasPrimaryExistingRepaymentBuffer = $rootScope.CurrentOrganization.hasPrimaryExistingRepaymentBufferAF();
      hasPrimaryProposedRepaymentBuffer = $rootScope.CurrentOrganization.hasPrimaryProposedRepaymentBufferAF();
    }
    if (workflow?.isHomeLoan()) {
      hasPrimaryIncomeBuffer = $rootScope.CurrentOrganization.hasPrimaryIncomeBufferHL();
      hasPrimaryExistingRepaymentBuffer = $rootScope.CurrentOrganization.hasPrimaryExistingRepaymentBufferHL();
      hasPrimaryProposedRepaymentBuffer = $rootScope.CurrentOrganization.hasPrimaryProposedRepaymentBufferHL();
    }
    if (hasPrimaryIncomeBuffer) {
      const rentalIncomeShadingBuffer = helperService.getPrimaryBuffer({
        workflow,
        bufferType: 'INCOME'
      });
      const val = helperService.round({ value: rentalIncomeShadingBuffer * 100 }).toFixed(2);
      announcement += `${val}% of rental income added to gross income, `;
    } else {
      const totalIncomeShadingBuffer = helperService.getSecondaryBuffer({
        workflow,
        bufferType: 'INCOME'
      });
      const val = helperService.round({ value: totalIncomeShadingBuffer * 100 }).toFixed(2);
      announcement += `${val}% of total income, `;
    }
    if (hasPrimaryExistingRepaymentBuffer) {
      const currentRepaymentPrimaryBuffer = helperService.getPrimaryBuffer({
        workflow,
        bufferType: 'EXISTING_REPAYMENT'
      });
      const val = currentRepaymentPrimaryBuffer.toFixed(2);
      announcement += `${val}x multiplier on current mortgage repayments, `;
    } else {
      const currentRepaymentSecondaryBuffer = helperService.getSecondaryBuffer({
        workflow,
        bufferType: 'EXISTING_REPAYMENT'
      });
      const val = helperService.round({ value: currentRepaymentSecondaryBuffer }).toFixed(2);
      announcement += currentRepaymentSecondaryBuffer
        ? `${val}% buffer on current mortgage rates, `
        : 'no loading on current mortgage rates, ';
    }
    if (hasPrimaryProposedRepaymentBuffer) {
      const proposedRepaymentPrimaryBuffer = helperService.getPrimaryBuffer({
        workflow,
        bufferType: 'PROPOSED_REPAYMENT'
      });
      const val = helperService
        .round({ value: proposedRepaymentPrimaryBuffer * 100 * 12 })
        .toFixed(2);
      announcement += `${val}% as floor rate on proposed loan rates.`;
    } else {
      const proposedRepaymentSecondaryBuffer = helperService.getSecondaryBuffer({
        workflow,
        bufferType: 'PROPOSED_REPAYMENT'
      });
      const val = helperService.round({ value: proposedRepaymentSecondaryBuffer }).toFixed(2);
      announcement += proposedRepaymentSecondaryBuffer
        ? `${val}% buffer on proposed loan rates.`
        : 'no buffer on proposed loan rates.';
    }
    return announcement;
  };

  /**
   * Prepare servicing liability.
   *
   * @param {Array} assets
   * @param {Array} liabilities
   * @param {Object} liabilityModel
   * @param {String} idLiabilityType
   * @param {String} idContact
   *
   * @returns {Object}
   */
  const prepareServicingLiabilities = ({
    assets,
    liabilities,
    liabilityModel,
    idLiabilityType,
    idContact
  }) => {
    const availableLiabilityTypes = Liability.getAvailableLiabilityTypes();
    const currentLiabilities = [];
    const filteredLiabilities = _.filter(
      liabilities || [],
      (l) => l?.liabilityType?.id === idLiabilityType
    );
    for (let l = 0; l < filteredLiabilities?.length; l += 1) {
      const liability = filteredLiabilities[l];
      const isReady =
        liability?.liabilityType?.id === availableLiabilityTypes[0]?.id
          ? !helperService.isLiabilityLinkedToAssetMarkedAsToBeSold({
              assets: assets || [],
              idAsset: liability.idAsset
            })
          : true;
      if (isReady) {
        const currentLiability = JSON.parse(JSON.stringify(liabilityModel));
        const percent = helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
          liability.ownership,
          idContact
        );
        currentLiability.key = liability?.name || '';
        currentLiability.isInvestment = liability?.mortgageType || false;
        currentLiability.isRefinanced = liability?.isRefinanced || false;
        currentLiability.lenderAbbr = liability?.name || '';
        currentLiability.repayment =
          (availableLiabilityTypes[1]?.id !== idLiabilityType ||
            availableLiabilityTypes[2]?.id !== idLiabilityType) &&
          percent > 0
            ? liability.repayment * (percent / 100)
            : 0;
        currentLiability.interestRate = liability?.interestRate || 0;
        currentLiability.balance =
          percent > 0 ? (liability?.balance || 0) * (percent / 100) : 0;
        currentLiability.limit = percent > 0 ? (liability?.limit || 0) * (percent / 100) : 0;
        currentLiability.termIo = 0;
        if (liability?.interestOnlyExpiryDate) {
          currentLiability.termIo = moment
            .utc(liability.interestOnlyExpiryDate, 'X')
            .diff(moment.utc(), 'months');
        }
        currentLiability.term = 0;
        if (liability?.endDate) {
          currentLiability.term = moment
            .utc(liability.endDate, 'X')
            .diff(moment.utc(), 'months');
        }
        currentLiabilities.push(currentLiability);
      }
    }
    return currentLiabilities;
  };

  /**
   * Prepare servicing liability.
   *
   * @param {Array} contacts
   * @param {Object} product
   * @param {Object} searchFields
   * @param {String} idContact
   *
   * @returns {Object}
   */
  const prepareServicingProposedLiabilities = ({
    contacts,
    product,
    searchFields,
    idContact
  }) => {
    const productHomeLoanModel = {
      key: '',
      idSplit: '',
      isInvestment: false,
      balance: 0,
      term: 0,
      termIo: 0,
      interestRate: 0,
      revertRate: 0,
      ownership: 0
    };
    const currentProposedLiabilities = [];
    if (product?.productSplits?.length) {
      for (let p = 0; p < product.productSplits.length; p += 1) {
        const productSplit = product?.productSplits[p];
        const currentProposedLiability = JSON.parse(JSON.stringify(productHomeLoanModel));
        currentProposedLiability.key = product?.id || '';
        currentProposedLiability.idSplit = productSplit?.id || '';
        currentProposedLiability.isInvestment = !productSplit?.isOwnerOccupied || false;
        currentProposedLiability.balance = productSplit?.totalLoanAmount || 0;
        currentProposedLiability.term = _.multiply(productSplit?.loanTerm || 0, 12) || 0;
        currentProposedLiability.termIo =
          _.multiply(
            searchFields?.interestOnlyTerm || productSplit?.interestOnlyTerm || 0,
            12
          ) || 0;
        currentProposedLiability.interestRate = productSplit?.interestRate || 0;
        currentProposedLiability.revertRate = productSplit?.revertRate || 0;
        currentProposedLiability.ownership = helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
          productSplit.ownership,
          idContact
        );
        currentProposedLiabilities.push(currentProposedLiability);
      }
    } else {
      const currentProposedLiability = JSON.parse(JSON.stringify(productHomeLoanModel));
      currentProposedLiability.key = 'search';
      currentProposedLiability.isInvestment = false;
      currentProposedLiability.balance = searchFields?.loanAmount || 0;
      currentProposedLiability.term = _.multiply(searchFields?.loanTerm || 0, 12) || 0;
      currentProposedLiability.termIo =
        _.multiply(searchFields?.interestOnlyTerm || 0, 12) || 0;
      currentProposedLiability.ownership = contacts?.length
        ? parseInt(100 / contacts.length, 10)
        : 0;
      currentProposedLiabilities.push(currentProposedLiability);
    }
    return currentProposedLiabilities;
  };

  /**
   * Prepare servicing data.
   *
   * @param {Array} lenders
   * @param {Array} products
   * @param {Array} product
   * @param {Array} contacts
   * @param {Array} income
   * @param {Object} expenses
   * @param {Array} assets
   * @param {Array} liabilities
   * @param {Array} homeLoanSecurities
   * @param {Array} ticketClientTypes
   * @param {Object} fundingWorksheets
   * @param {Object} workflow
   * @param {Object} owner
   * @param {Object} searchFields
   * @param {Boolean} isDealOwnerBroker
   *
   * @returns {Object}
   */
  const prepareEstimateData = ({
    lenders,
    products,
    contacts,
    income,
    expenses,
    assets,
    liabilities,
    homeLoanSecurities,
    ticketClientTypes,
    fundingWorksheets,
    financeProposal,
    workflow,
    owner,
    searchFields,
    isDealOwnerBroker,
    isSearched,
    useGeneric
  }) => {
    const contactModel = {
      key: '',
      keySpouse: '',
      state: '',
      postalCode: '',
      married: false,
      dependents: 0,
      totalIncomeRental: 0,
      totalIncomeOtherTaxable: 0,
      totalIncomeNonTaxable: 0,
      totalIncomeBusinessLastTaxYear: 0,
      totalIncomeBusinessPreviousTaxYear: 0,
      totalInterestAndOtherAddBacksLastTaxYear: 0,
      totalInterestAndOtherAddBacksPreviousTaxYear: 0,
      totalProfitBeforeTaxLastTaxYear: 0,
      totalProfitBeforeTaxPreviousTaxYear: 0,
      totalDepreciationLastTaxYear: 0,
      totalDepreciationPreviousTaxYear: 0,
      liabilitiesCreditCards: 0,
      liabilitiesMortgage: [],
      liabilitiesPersonal: [],
      liabilitiesVehicle: [],
      liabilitiesOther: [],
      hasEducationDebt: false,
      isSelfEmployedInACompany: false,
      housing: 0,
      purchaseOwnerOccupiedStatus: false,
      postSettlementAddressStatus: false,
      splitOwnership: 0
    };
    const liabilityModel = {
      key: '',
      isInvestment: false,
      isRefinanced: false,
      lenderAbbr: '',
      repayment: 0,
      interestRate: 0,
      balance: 0,
      limit: 0,
      term: 0
    };
    const expenseModel = {
      key: '',
      idContacts: [],
      foodAndSupermarket: 0,
      coffeesLunchesTakeaway: 0,
      entertainment: 0,
      domesticHolidays: 0,
      gymFeesSportOtherHealthAndWellness: 0,
      cigarettesAndAlcohol: 0,
      clothingShoesAndAccessories: 0,
      hairdressingAndGrooming: 0,
      phoneInternetAndPayTv: 0,
      mediaStreamingAndSubscriptionServices: 0,
      giftsAndCelebrations: 0,
      otherDiscretionaryExpenses: 0,
      recreationalVehicleRunningCosts: 0,
      pets: 0,
      publicEducationCosts: 0,
      privateEducationCosts: 0,
      tertiaryAndVocationalEducation: 0,
      childcare: 0,
      childOrSpousalMaintenance: 0,
      privateHealthInsurance: 0,
      doctorDentistPharmacyGlasses: 0,
      lifeTraumaIncomeInsurance: 0,
      essentialVehicleRunningCost: 0,
      publicTransportTaxisAndRideShareCommutingAirfares: 0,
      essentialVehicleInsurance: 0,
      currentRentExpense: 0,
      ongoingBoardExpense: 0,
      primaryResidenceRunningCosts: 0,
      primaryResidenceBodyCorp: 0,
      secondaryResidenceRunningCosts: 0,
      secondaryResidenceBodyCorp: 0,
      investmentPropertyRunningCosts: 0,
      investmentPropertyBodyCorp: 0
    };
    const productAssetFinanceModel = {
      key: '',
      rateDiscount: 0,
      balloonValue: 0,
      commissionValue: 0,
      payments: 0,
      paymentFrequency: '',
      term: 0,
      amount: 0,
      interestRate: 0
    };
    const productHomeLoanModel = {
      key: '',
      balance: 0,
      term: 0,
      termIo: 0,
      interestRate: 0,
      revertRate: 0,
      ownership: 0
    };
    const availableIncomeTypes = incomeService.getAvailableIncomeTypes();
    const availableLiabilityTypes = Liability.getAvailableLiabilityTypes();
    const availablePaymentFrequencies = ASSET_FINANCE.DETAILS.PAYMENT_FREQUENCY;
    const estimateData = {};
    const edProducts = [];
    const edExpenses = [];
    const edContacts = [];
    estimateData.key = 'search';
    if (workflow?.isAssetFinance()) {
      estimateData.isSearched = isSearched || false;
      const pLen = products?.length || 0;
      if (pLen) {
        for (let p = 0; p < pLen; p += 1) {
          const currentProduct = products[p];
          const edProduct = JSON.parse(JSON.stringify(productAssetFinanceModel));
          const paymentFrequency = _.find(
            availablePaymentFrequencies,
            (f) => f?.id === currentProduct?.productDetails?.paymentFrequency
          );
          edProduct.key = currentProduct?.id || 'product';
          edProduct.rateDiscount = helperService.round({
            value: currentProduct?.productDetails?.rateDiscount || 0
          });
          edProduct.balloonValue = helperService.round({
            value: currentProduct?.productDetails?.balloonValue || 0
          });
          edProduct.commissionValue = helperService.round({
            value: currentProduct?.productDetails?.brokerCommissionValue || 0
          });
          edProduct.payments = (currentProduct?.productDetails?.payments ? 1 : 0) || 0;
          edProduct.paymentFrequency =
            paymentFrequency?.llField || availablePaymentFrequencies[1].llField;
          edProduct.term = currentProduct?.productDetails?.term || 0;
          edProduct.amount = helperService.round({
            value: currentProduct?.productDetails?.netAmountFinanced || 0
          });
          edProducts.push(edProduct);
        }
      } else {
        const edProduct = JSON.parse(JSON.stringify(productAssetFinanceModel));
        const paymentFrequency = _.find(
          availablePaymentFrequencies,
          (f) => f?.id === financeProposal?.paymentFrequency
        );
        edProduct.key = financeProposal?.id || 'product';
        edProduct.rateDiscount = !isSearched
          ? helperService.round({
              value: financeProposal?.rateDiscount || 0
            })
          : 0;
        edProduct.balloonValue = !isSearched
          ? helperService.round({
              value: financeProposal?.balloonValue || 0
            })
          : searchFields?.balloonValue || 0;
        edProduct.commissionValue = !isSearched
          ? helperService.round({
              value: financeProposal?.brokerCommissionValue || 0
            })
          : 0;
        edProduct.payments = (financeProposal?.payments ? 1 : 0) || 0;
        edProduct.paymentFrequency =
          paymentFrequency?.llField || availablePaymentFrequencies[1].llField;
        edProduct.term = !isSearched ? financeProposal?.term || 0 : searchFields?.loanTerm || 0;
        edProduct.amount = !isSearched
          ? helperService.round({
              value: financeProposal?.netAmountFinanced || 0
            })
          : searchFields?.loanAmount || 0;
        edProducts.push(edProduct);
      }
    }
    if (workflow?.isHomeLoan()) {
      let totalLvr = 0;
      if (fundingWorksheets?.fundsAvailable?.length) {
        const tLvr = _.compact(_.map(fundingWorksheets?.fundsAvailable, 'totalLvr'));
        totalLvr = tLvr?.length ? _.max(tLvr) : 0;
      }
      estimateData.lvr = helperService.round({ value: totalLvr });
      const loanAmount = _.reduce(
        products,
        (memo, num) => memo + helperService.isNumber(num?.totalLoanAmount || 0, 0),
        0
      );
      estimateData.amount = searchFields?.loanAmount || loanAmount || 0;
      estimateData.useLmi = _.some(fundingWorksheets?.fundsAvailable, (f) => f.lmi > 0);
      estimateData.useGeneric = useGeneric || false;
    }
    const filteredContacts = _.filter(
      contacts,
      (c) =>
        !helperService.isClientSecurityGuarantor({
          ticketClientTypes,
          idContact: c?.id
        }) &&
        !helperService.isClientNonBorrowingSpouse({
          ticketClientTypes,
          idContact: c?.id
        }) &&
        !c.isCompany
    );
    const cLen = filteredContacts?.length || 0;
    for (let c = 0; c < cLen; c += 1) {
      const contact = filteredContacts[c];
      const [, cca2] = getOrganizationCountry();
      const [state, postCode] = getStateAndPostCodePerContact(
        assets || [],
        homeLoanSecurities || [],
        contact,
        owner,
        $rootScope.CurrentOrganization,
        cca2
      );
      const edContact = JSON.parse(JSON.stringify(contactModel));
      edContact.key = contact?.id || 'x';
      edContact.keySpouse = contact?.person?.information?.idSpouse || 'y';
      edContact.state = state;
      edContact.postCode = postCode;
      edContact.married =
        contact?.person?.information?.maritalStatus === 2 ||
        contact?.person?.information?.maritalStatus === 4 ||
        contact?.person?.information?.maritalStatus?.name ===
          ContactMaritalStatusOption.Married.name ||
        contact?.person?.information?.maritalStatus?.name ===
          ContactMaritalStatusOption.DeFacto.name ||
        false;
      edContact.dependents = contact?.person?.information?.numberOfDependents || 0;
      const existingInvestmentProperties = helperService.getInvestmentAssets({
        assets,
        homeLoanSecurities
      });
      let existingOwnerOccupiedProperties = [];
      if (helperService.getOrganizationCountry().cca2 === 'NZ') {
        existingOwnerOccupiedProperties = helperService.getOwnerOccupiedAssets({
          assets,
          homeLoanSecurities
        });
      }
      const newInvestmentProperties = helperService.getInvestmentHomeLoanSecurities({
        homeLoanSecurities
      });
      edContact.totalIncomeGrossSalary = incomeService.getTotalPaygIncomePerContact(
        income,
        contact.id,
        'grossSalary',
        'grossSalaryFrequency'
      );
      edContact.totalIncomeAllowance = incomeService.getTotalPaygIncomePerContact(
        income,
        contact.id,
        'allowance',
        'allowanceFrequency'
      );
      edContact.totalIncomeBonus = incomeService.getTotalPaygIncomePerContact(
        income,
        contact.id,
        'bonus',
        'bonusFrequency'
      );
      edContact.totalIncomeCommissions = incomeService.getTotalPaygIncomePerContact(
        income,
        contact.id,
        'commission',
        'commissionFrequency'
      );
      edContact.totalIncomeOvertimeEssential = incomeService.getTotalPaygIncomePerContact(
        income,
        contact.id,
        'overtimeEssential',
        'overtimeEssentialFrequency'
      );
      edContact.totalIncomeOvertimeNonEssential = incomeService.getTotalPaygIncomePerContact(
        income,
        contact.id,
        'overtimeNonEssential',
        'overtimeNonEssentialFrequency'
      );
      edContact.totalIncomeRental = incomeService.getTotalIncomeByContactByIncomeType({
        income,
        incomeType: 'rental',
        idContact: contact.id,
        assets,
        homeLoanSecurities,
        workflowType: workflow?.type,
        isDealOwnerBroker,
        haveLendersExisting: lenders.length > 0,
        isUsingForFinanceCalculations: true
      });
      edContact.totalIncomeRentalProperties =
        existingInvestmentProperties.length + newInvestmentProperties.length;
      edContact.totalIncomeBoarder = 0;
      if (helperService.getOrganizationCountry().cca2 === 'NZ') {
        edContact.totalIncomeBoarder = incomeService.getTotalIncomeByContactByIncomeType({
          income,
          incomeType: 'boarder',
          idContact: contact.id,
          assets,
          homeLoanSecurities,
          workflowType: workflow.type,
          isDealOwnerBroker,
          haveLendersExisting: lenders.length > 0,
          isUsingForFinanceCalculations: true
        });
        edContact.totalIncomeBoarderNumberOfBoarders = _.reduce(
          existingOwnerOccupiedProperties,
          (memo, num) => memo + helperService.isNumber(num.numberOfBoarders, true),
          0
        );
        edContact.totalIncomeBoarderAllInclusive = _.some(
          existingOwnerOccupiedProperties,
          (o) => o.allInclusive
        );
      }
      edContact.totalIncomeOtherTaxable = incomeService.getTotalIncomeByContactByIncomeType({
        income,
        incomeType: availableIncomeTypes[2].id,
        idContact: contact.id,
        assets,
        homeLoanSecurities,
        workflowType: workflow.type,
        isDealOwnerBroker,
        haveLendersExisting: lenders.length > 0,
        isUsingForFinanceCalculations: true
      });
      edContact.totalIncomeNonTaxable = incomeService.getTotalIncomeByContactByIncomeType({
        income,
        incomeType: availableIncomeTypes[3].id,
        idContact: contact.id,
        assets,
        homeLoanSecurities,
        workflowType: workflow.type,
        isDealOwnerBroker,
        haveLendersExisting: lenders.length > 0,
        isUsingForFinanceCalculations: true
      });
      edContact.totalIncomeBusinessLastTaxYear = incomeService.getBusinessYear({
        income,
        idContact: contact.id,
        businessYear: 'lastTaxYear'
      });
      edContact.totalIncomeBusinessPreviousTaxYear = incomeService.getBusinessYear({
        income,
        idContact: contact.id,
        businessYear: 'previousTaxYear'
      });

      edContact.totalProfitBeforeTaxLastTaxYear = incomeService.getTotalBusinessIncomePerContact(
        income,
        contact.id,
        edContact.totalIncomeBusinessLastTaxYear,
        'lastTaxYear',
        'profitBeforeTax'
      );
      edContact.totalInterestAndOtherAddBacksLastTaxYear = incomeService.getTotalBusinessIncomePerContact(
        income,
        contact.id,
        edContact.totalIncomeBusinessLastTaxYear,
        'lastTaxYear',
        'interestAndOtherAddbacks'
      );
      edContact.totalDepreciationLastTaxYear = incomeService.getTotalBusinessIncomePerContact(
        income,
        contact.id,
        edContact.totalIncomeBusinessLastTaxYear,
        'lastTaxYear',
        'depreciation'
      );
      if (helperService.getOrganizationCountry().cca2 === 'AU') {
        edContact.totalProfitBeforeTaxPreviousTaxYear = incomeService.getTotalBusinessIncomePerContact(
          income,
          contact.id,
          edContact.totalIncomeBusinessPreviousTaxYear,
          'previousTaxYear',
          'profitBeforeTax'
        );
        edContact.totalInterestAndOtherAddBacksPreviousTaxYear = incomeService.getTotalBusinessIncomePerContact(
          income,
          contact.id,
          edContact.totalIncomeBusinessPreviousTaxYear,
          'previousTaxYear',
          'interestAndOtherAddbacks'
        );
        edContact.totalDepreciationPreviousTaxYear = incomeService.getTotalBusinessIncomePerContact(
          income,
          contact.id,
          edContact.totalIncomeBusinessPreviousTaxYear,
          'previousTaxYear',
          'depreciation'
        );
      } else {
        edContact.totalProfitBeforeTaxPreviousTaxYear = 0;
        edContact.totalInterestAndOtherAddBacksPreviousTaxYear = 0;
        edContact.totalDepreciationPreviousTaxYear = 0;
      }
      edContact.liabilitiesCreditCards = prepareServicingLiabilities({
        liabilityModel,
        liabilities,
        idContact: contact.id,
        idLiabilityType: availableLiabilityTypes[1]?.id
      });
      edContact.liabilitiesOverdraft = prepareServicingLiabilities({
        liabilityModel,
        liabilities,
        idContact: contact.id,
        idLiabilityType: availableLiabilityTypes[2]?.id
      });
      edContact.liabilitiesMortgage = prepareServicingLiabilities({
        assets,
        liabilityModel,
        liabilities,
        idContact: contact.id,
        idLiabilityType: availableLiabilityTypes[0]?.id
      });
      edContact.liabilitiesPersonal = prepareServicingLiabilities({
        liabilityModel,
        liabilities,
        idContact: contact.id,
        idLiabilityType: availableLiabilityTypes[4]?.id
      });
      edContact.liabilitiesVehicle = prepareServicingLiabilities({
        liabilityModel,
        liabilities,
        idContact: contact.id,
        idLiabilityType: availableLiabilityTypes[3]?.id
      });
      edContact.liabilitiesOther = prepareServicingLiabilities({
        liabilityModel,
        liabilities,
        idContact: contact.id,
        idLiabilityType: availableLiabilityTypes[7]?.id
      });
      edContact.liabilitiesProposed = prepareServicingProposedLiabilities({
        products,
        contacts,
        productHomeLoanModel,
        searchFields,
        idContact: contact.id
      });
      if (estimateData.useGeneric && fundingWorksheets?.fundsRequired?.length) {
        const isInvestment = _.some(
          fundingWorksheets.fundsRequired,
          (f) => f?.ownershipType?.name === OwnershipType.investment.name
        );
        const balance = _.reduce(
          fundingWorksheets.fundsAvailable,
          (sum, n) => _.add(sum, helperService.isNumber(n?.totalProposedLoanAmount, true)),
          0
        );
        for (let l = 0; l < edContact.liabilitiesProposed?.length; l += 1) {
          edContact.liabilitiesProposed[l].isInvestment = isInvestment;
          edContact.liabilitiesProposed[l].balance = balance;
        }
      }
      edContact.isSelfEmployedInACompany = helperService.isSelfEmployedInACompany({
        contact
      });
      edContact.housing = contact?.housing || contact?.person?.contact?.settlementHousing || 0;
      edContact.purchaseOwnerOccupiedStatus = getPurchaseOwnerOccupiedStatus(
        [contact],
        assets,
        homeLoanSecurities,
        fundingWorksheets,
        false
      );
      edContact.hasEducationDebt = helperService.hasEducationDebtPerContact({
        liabilities,
        idContact: contact.id
      });
      edContact.postSettlementAddressStatus = helperService.getPostSettlementStatus({
        assets,
        homeLoanSecurities,
        contact
      });

      edContacts.push(edContact);
    }

    const getExpenseValue = ({ householdExpenses, type }) => {
      const expense = _.find(householdExpenses, (h) => h?.type?.name === type?.name);
      if (expense) {
        return helperService.round({
          value:
            _.multiply(
              expense?.value,
              helperService.getFrequencyValue(expense?.frequency, true)
            ) || 0
        });
      }
      return 0;
    };
    const isHouseholdComposedOnlyOfSecurityGuarantorClients = ({ idContacts }) => {
      const idGuarantors = [];
      const len = idContacts?.length || 0;
      for (let c = 0; c < len; c += 1) {
        const idContact = idContacts[c];
        if (
          helperService.isClientSecurityGuarantor({
            ticketClientTypes,
            idContact
          })
        ) {
          idGuarantors.push(idContact);
        }
      }
      return idGuarantors.length > 0 && idGuarantors.length === idContacts.length;
    };
    const householdExpensesTypes = HouseholdExpenseType.available();
    const eLen = expenses?.length || 0;
    if (eLen) {
      for (let e = 0; e < eLen; e += 1) {
        const edExpense = JSON.parse(JSON.stringify(expenseModel));
        const household = expenses[e];
        if (
          !isHouseholdComposedOnlyOfSecurityGuarantorClients({
            idContacts: household?.idContacts
          })
        ) {
          edExpense.key = household?.id || 'h';
          edExpense.idContacts = household?.idContacts || [];
          edExpense.foodAndSupermarket = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[0]
          });
          edExpense.coffeesLunchesTakeaway = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[1]
          });
          edExpense.entertainment = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[2]
          });
          edExpense.domesticHolidays = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[3]
          });
          edExpense.gymFeesSportOtherHealthAndWellness = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[4]
          });
          edExpense.cigarettesAndAlcohol = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[5]
          });
          edExpense.clothingShoesAndAccessories = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[6]
          });
          edExpense.hairdressingAndGrooming = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[7]
          });
          edExpense.phoneInternetAndPayTv = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[8]
          });
          edExpense.mediaStreamingAndSubscriptionServices = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[9]
          });
          edExpense.giftsAndCelebrations = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[10]
          });
          edExpense.otherDiscretionaryExpenses = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[11]
          });
          edExpense.recreationalVehicleRunningCosts = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[12]
          });
          edExpense.pets = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[13]
          });
          edExpense.publicEducationCosts = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[14]
          });
          edExpense.privateEducationCosts = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[15]
          });
          edExpense.tertiaryAndVocationalEducation = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[16]
          });
          edExpense.childcare = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[17]
          });
          edExpense.childOrSpousalMaintenance = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[18]
          });
          edExpense.privateHealthInsurance = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[19]
          });
          edExpense.doctorDentistPharmacyGlasses = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[20]
          });
          edExpense.lifeTraumaIncomeInsurance = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[21]
          });
          edExpense.essentialVehicleRunningCost = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[22]
          });
          edExpense.publicTransportTaxisAndRideShareCommutingAirfares = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[23]
          });
          edExpense.essentialVehicleInsurance = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[24]
          });
          edExpense.currentRentExpense = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[25]
          });
          edExpense.ongoingBoardExpense = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[26]
          });
          edExpense.primaryResidenceRunningCosts = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[27]
          });
          edExpense.primaryResidenceBodyCorp = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[28]
          });
          edExpense.secondaryResidenceRunningCosts = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[29]
          });
          edExpense.secondaryResidenceBodyCorp = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[30]
          });
          edExpense.investmentPropertyRunningCosts = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[31]
          });
          edExpense.investmentPropertyBodyCorp = getExpenseValue({
            householdExpenses: household?.householdExpenses || [],
            type: householdExpensesTypes[32]
          });
          edExpenses.push(edExpense);
        }
      }
    } else {
      const edExpense = JSON.parse(JSON.stringify(expenseModel));
      edExpense.key = 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa';
      edExpense.idContacts = ['hhhhhhhh-hhhh-hhhh-hhhh-hhhhhhhhhhhh'];
      edExpenses.push(edExpense);
    }
    estimateData.products = edProducts;
    estimateData.contacts = edContacts;
    estimateData.expenses = edExpenses;
    return estimateData;
  };

  return {
    getMonthlyFinancialPositionAnnouncement,
    prepareServicingProposedLiabilities,
    prepareEstimateData
  };
};
